import React, { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";

import { Auth } from "aws-amplify";
import { CognitoIdToken, CognitoRefreshToken, CognitoAccessToken, CognitoUserSession } from "amazon-cognito-identity-js";

import { AppEvent, appEventEmitter, triggerAppEvent, useAppContext } from "./app.context";
import {
  VerificationCodeRequest,
  LoginRequest,
  RegisterRequest,
  VerifyResetRequest,
  VerifyRegisterRequest,
  ChangePasswordRequest,
} from "../models/login-request.model";
import errorMessages from "../translations/en/errors.json";
import usePersistedState from "../hooks/persisted-state.hook";
import { AuthSession } from "../models/auth-session.model";
import { useLocation, useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import queryString from "query-string";
import axios from "axios";
import environment from "../env";
import { SsoExchangeResponse } from "../models/sso.model";

// Types

export interface AuthContext {
  session?: AuthSession | null;
  setSession(session: (AuthSession & { username: string }) | null): void;
  login(request: LoginRequest): Promise<void>;
  register(request: RegisterRequest): Promise<void>;
  refresh(): Promise<AuthSession>;
  verifySignUp(request: VerifyRegisterRequest): Promise<void>;
  resendSignUpCode(request: VerificationCodeRequest): Promise<void>;
  resetPassword(request: VerificationCodeRequest): Promise<void>;
  verifyResetPassword(request: VerifyResetRequest): Promise<void>;
  changePassword(request: ChangePasswordRequest): Promise<void>;
  signOut(): Promise<void>;
  isAuthReady: boolean;
}

const Context = createContext<AuthContext>(null!);

const { defaultClientId, idpClientId, userPoolId, domain: cognitoDomain } = environment.cognito;

// SSO state helpers

interface SsoStatePayload {
  csrf: string;
  providerName: string;
  email: string;
}

/**
 * Decodes a base64-encoded state string produced by LoginForm during app-initiated SSO.
 * Returns null if the string is missing, malformed, or not our format.
 */
function parseSsoState(state: string | undefined | null): SsoStatePayload | null {
  if (!state) return null;
  try {
    const decoded = JSON.parse(atob(state));
    if (
      typeof decoded?.csrf !== "string" ||
      typeof decoded?.providerName !== "string" ||
      typeof decoded?.email !== "string"
    ) return null;
    return decoded as SsoStatePayload;
  } catch {
    return null;
  }
}

// Provider

function AuthContextProvider(props: React.PropsWithChildren<{}>): JSX.Element {
  const history = useHistory();
  const { search } = useLocation();
  const queryClient = useQueryClient();

  const [authParams] = useState<{ code?: string; state?: string }>(queryString.parse(search) as any);

  const [isAuthReady, setIsAuthReady] = useState(false);

  const [session, setSession] = usePersistedState<(AuthSession & { username: string }) | null>("context/session", {
    storageType: "session",
    priority: 1, // This should be the highest priory in the app as it is needed to get in
    clearStateHandler: { emitter: appEventEmitter, event: AppEvent.signOut },
  });

  const sessionRef = useRef(session || null);
  const setSessionRef = useRef(setSession);
  useEffect(() => {
    setSessionRef.current = setSession;
  }, [setSession]);

  useEffect(() => {
    setIsAuthReady(false);

    // Configure Amplify (still needed for register, signOut, reset, etc.)
    const userClientType = sessionRef.current?.clientType ?? "app";
    Auth.configure({
      Analytics: { disabled: true },
      Auth: {
        oauth: {
          scope: ["email"],
          responseType: "code",
        },
        region: "us-east-1",
        userPoolId,
        userPoolWebClientId: userClientType === "microsoftIdp" && idpClientId ? idpClientId : defaultClientId,
        storage: window.sessionStorage,
        authenticationFlowType: "USER_PASSWORD_AUTH",
      },
    });

    const hasAuthCode = Boolean(authParams?.code);

    if (hasAuthCode) {
      history.replace("");

      const ssoState = parseSsoState(authParams.state);

      if (ssoState) {
        // App-initiated SSO: validate CSRF then exchange code via the BE.
        const storedCsrf = sessionStorage.getItem("sso_csrf");
        sessionStorage.removeItem("sso_csrf");

        if (storedCsrf && ssoState.csrf !== storedCsrf) {
          // CSRF mismatch -- possible CSRF attack or stale tab; go back to login.
          history.replace("/login");
          setIsAuthReady(true);
          return;
        }

        axios
          .post<SsoExchangeResponse>(
            "auth/sso/exchange",
            { code: authParams.code, providerName: ssoState.providerName, email: ssoState.email },
            { baseURL: environment.newApiUrl }
          )
          .then((response) => {
            const { username, accessToken, issuedAt, expiresIn, refreshToken } = response.data;
            setSessionRef.current({ username, clientType: "microsoftIdp", accessToken, issuedAt, expiresIn, refreshToken });
          })
          .catch((error) => {
            console.error("[auth] SSO exchange failed:", error);
            history.replace("/login");
          })
          .finally(() => setIsAuthReady(true));
      } else if (idpClientId) {
        // IdP-initiated SSO (Azure portal tile click): exchange code directly with Cognito.
        // Preserved for backward compatibility -- no state means this came from the IdP, not our login page.
        const urlencoded = new URLSearchParams();
        urlencoded.append("grant_type", "authorization_code");
        urlencoded.append("client_id", idpClientId);
        urlencoded.append("code", authParams.code!);
        urlencoded.append("redirect_uri", window.location.origin);

        fetch(`${cognitoDomain}/oauth2/token`, { method: "POST", body: urlencoded, redirect: "follow" })
          .then((response) => response.json())
          .then((result) => {
            if (!result.id_token || !result.access_token || !result.refresh_token) {
              return Promise.reject(new Error("Invalid token response: missing required fields"));
            }
            const localSession = new CognitoUserSession({
              IdToken: new CognitoIdToken({ IdToken: result.id_token }),
              RefreshToken: new CognitoRefreshToken({ RefreshToken: result.refresh_token }),
              AccessToken: new CognitoAccessToken({ AccessToken: result.access_token }),
            });
            setSessionRef.current({
              username: localSession.getAccessToken().payload.username,
              clientType: "microsoftIdp",
              accessToken: localSession.getIdToken().getJwtToken(),
              issuedAt: localSession.getIdToken().getIssuedAt(),
              expiresIn: localSession.getIdToken().getExpiration(),
              refreshToken: localSession.getRefreshToken().getToken(),
            });
          })
          .catch((error) => console.error("[auth] IdP-initiated exchange failed:", error))
          .finally(() => setIsAuthReady(true));
      } else {
        // Code present but no IdP configured and no app-initiated state -- discard.
        history.replace("/login");
        setIsAuthReady(true);
      }
    } else {
      // No auth code -- try to restore an existing Amplify session.
      Auth.currentSession()
        .catch(() => null)
        .then((response) => {
          if (response) {
            const accessToken = response.getIdToken().getJwtToken();
            const username = response.getAccessToken().payload.username;
            setSessionRef.current((prev) => {
              const clientType = sessionRef.current?.clientType ?? "app";
              if (prev?.accessToken === accessToken) return { ...prev, clientType };
              return {
                username,
                clientType,
                accessToken,
                expiresIn: response.getIdToken().getExpiration(),
                issuedAt: response.getIdToken().getIssuedAt(),
                refreshToken: response.getRefreshToken().getToken(),
              };
            });
          }
        })
        .finally(() => setIsAuthReady(true));
    }
  }, [authParams, history]);

  useEffect(() => {
    sessionRef.current = session || null;
  }, [session]);

  // Auth operations

  /**
   * Password login -- calls the BE, which calls Cognito.
   * The BE also enforces SSO-required rules before invoking Cognito.
   */
  const login = useCallback(
    async ({ username, password }: LoginRequest) => {
      const response = await axios.post<Omit<AuthSession, "username" | "clientType">>(
        "auth/login",
        { username, password },
        { baseURL: environment.newApiUrl }
      );
      setSession({
        username,
        clientType: "app",
        accessToken: response.data.accessToken,
        issuedAt: response.data.issuedAt,
        expiresIn: response.data.expiresIn,
        refreshToken: response.data.refreshToken,
      });
    },
    [setSession]
  );

  const register = useCallback(({ username, password, name }: RegisterRequest) => {
    return Auth.signUp({
      username,
      password,
      attributes: { name, email: username },
    }).then(() => Promise.resolve());
  }, []);

  const refresh = useCallback((): Promise<AuthSession> => {
    if (!sessionRef.current) {
      return Promise.reject(errorMessages.auth.unauthorized);
    }
    const { username, refreshToken, clientType } = sessionRef.current;
    const userClientType = clientType ?? "app";
    return axios
      .post<AuthSession>("auth/refresh", { username, refreshToken, clientType: userClientType }, { baseURL: environment.newApiUrl })
      .then((response) => {
        setSession({ ...response.data, username, clientType: userClientType });
        return response.data;
      })
      .catch((error) => {
        setSession(null);
        throw error;
      });
  }, [setSession]);

  const resetPassword = useCallback((request: VerificationCodeRequest) => {
    return Auth.forgotPassword(request.username).then(() => Promise.resolve());
  }, []);

  const verifyResetPassword = useCallback(
    (request: VerifyResetRequest) => {
      return Auth.forgotPasswordSubmit(request.username, request.verificationCode, request.password).then(() => login(request));
    },
    [login]
  );

  const verifySignUp = useCallback(
    (request: VerifyRegisterRequest) => {
      return Auth.confirmSignUp(request.username, request.verificationCode).then(() => login(request));
    },
    [login]
  );

  const resendSignUpCode = useCallback((request: VerificationCodeRequest) => {
    return Auth.resendSignUp(request.username).then(() => Promise.resolve());
  }, []);

  const signOut = useCallback(() => {
    return Auth.signOut({ global: true })
      .catch(() => null)
      .then(() => {
        triggerAppEvent(AppEvent.signOut);
        setSession(null);
        queryClient.clear();
      });
  }, [setSession, queryClient]);

  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    const authUser = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(authUser, request.password, request.newPassword);
  }, []);

  const contextValue: AuthContext = {
    session,
    setSession,
    isAuthReady,
    login,
    register,
    refresh,
    verifySignUp,
    resendSignUpCode,
    resetPassword,
    verifyResetPassword,
    signOut,
    changePassword,
  };

  return <Context.Provider value={{ ...contextValue }}>{props.children}</Context.Provider>;
}

function useAuthContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(errorMessages.context.useHookWithinProvider);
  }
  return context;
}

export { AuthContextProvider, useAuthContext };